import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { DriverApi, DriversFilter } from '../models';
import { DriverStatus } from '../enums';
import { API_BUSINESS_URL } from '@/utils';

export type DriverDocumentSide = 'front' | 'back';

export type DriverDocumentResponse = {
    imageUrl: string | null;
    driverId: string;
    documentTypeId: string;
    side: DriverDocumentSide;
};

export type DocumentTypeApi = {
    id: string;
    name: string;
};

export type GetDriversQueryArgs = {
    filters: DriversFilter;
    paginated: PaginatedRequest;
};

const driversService = createApi({
    reducerPath: 'businessDriversService',
    baseQuery: createBaseQueryWithReauth(API_BUSINESS_URL),
    tagTypes: ['Drivers', 'DocumentTypes', 'ProfilePicture', 'DriverDocument'],
    endpoints: (builder) => ({
        getDrivers: builder.query<PaginatedResponse<DriverApi>, GetDriversQueryArgs>({
            query: ({ paginated, filters }: GetDriversQueryArgs) => {
                const {
                    search,
                    email,
                    phone,
                    status,
                    currentOperatorId,
                    assignableToOperatorId,
                    assignableToRouteOperatorId,
                } = filters;

                const params: Record<string, string | number> = {
                    limit: paginated.pageSize,
                    offset: (paginated.page - 1) * paginated.pageSize,
                };

                if (search && search.trim() !== '') {
                    params.name = search.trim();
                }
                if (email && email.trim() !== '') {
                    params.email = email.trim();
                }
                if (phone && phone.trim() !== '') {
                    params.phone = phone.trim();
                }
                if (status && status.trim() !== '') {
                    params.status = status.trim();
                } else {
                    params.excludeStatus = DriverStatus.DELETED;
                }
                if (currentOperatorId && currentOperatorId.trim() !== '') {
                    params.currentOperatorId = currentOperatorId.trim();
                }
                if (assignableToOperatorId && assignableToOperatorId.trim() !== '') {
                    params.assignableToOperatorId = assignableToOperatorId.trim();
                }
                if (
                    assignableToRouteOperatorId &&
                    assignableToRouteOperatorId.trim() !== ''
                ) {
                    params.assignableToRouteOperatorId =
                        assignableToRouteOperatorId.trim();
                }

                const headers: Record<string, string> = {};
                if (currentOperatorId && currentOperatorId.trim() !== '') {
                    headers['x-logistic-operator-id'] = currentOperatorId.trim();
                }

                return {
                    url: '/drivers',
                    params,
                    ...(Object.keys(headers).length > 0 ? { headers } : {}),
                };
            },
            providesTags: ['Drivers'],
        }),

        getDriverById: builder.query<DriverApi, { id: string }>({
            query: ({ id }) => ({
                url: `/drivers/${id}`,
            }),
            providesTags: ['Drivers'],
        }),
        getDriverProfilePicture: builder.query<
            { imageUrl: string; driverId: string },
            { id: string }
        >({
            query: ({ id }) => ({
                url: `/drivers/${id}/profile-picture`,
            }),
            providesTags: (_result, _error, { id }) => [{ type: 'ProfilePicture', id }],
        }),
        uploadDriverProfilePicture: builder.mutation<void, { id: string; formData: FormData }>({
            query: ({ id, formData }) => ({
                url: `/drivers/${id}/profile-picture`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'ProfilePicture', id }],
        }),
        getDriverDocument: builder.query<
            DriverDocumentResponse,
            { driverId: string; documentTypeId: string; side: DriverDocumentSide }
        >({
            query: ({ driverId, documentTypeId, side }) => ({
                url: `/drivers/${driverId}/documents/${documentTypeId}/${side}`,
            }),
            providesTags: (_result, _error, { driverId, documentTypeId, side }) => [
                { type: 'DriverDocument', id: `${driverId}-${documentTypeId}-${side}` },
            ],
        }),
        uploadDriverDocument: builder.mutation<
            void,
            { driverId: string; documentTypeId: string; side: DriverDocumentSide; formData: FormData }
        >({
            query: ({ driverId, formData }) => ({
                url: `/drivers/${driverId}/documents`,
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: (_result, _error, { driverId, documentTypeId, side }) => [
                { type: 'DriverDocument', id: `${driverId}-${documentTypeId}-${side}` },
            ],
        }),
        getDocumentTypes: builder.query<DocumentTypeApi[], void>({
            query: () => ({ url: '/document-types' }),
            transformResponse: (response: DocumentTypeApi[] | { items?: DocumentTypeApi[] }) =>
                Array.isArray(response) ? response : response?.items ?? [],
            providesTags: ['DocumentTypes'],
        }),
        enableDriver: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `/drivers/${id}/enable`,
                method: 'POST',
            }),
            invalidatesTags: ['Drivers'],
        }),
        disableDriver: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `/drivers/${id}/disable`,
                method: 'POST',
            }),
            invalidatesTags: ['Drivers'],
        }),
        sendOrResendDriverLogisticOperatorOtp: builder.mutation<
            void,
            { driverId: string; logisticOperatorId: string }
        >({
            query: ({ driverId, logisticOperatorId }) => ({
                url: '/driver-logistic-operator-otp/send-or-resend-code',
                method: 'POST',
                body: {
                    driverId,
                    logisticOperatorId,
                },
            }),
            invalidatesTags: ['Drivers'],
        }),
        updateDriver: builder.mutation<
            DriverApi,
            {
                id: string;
                body: {
                    firstName: string;
                    lastName: string;
                    email: string;
                    documentNumber: string;
                    birthDate?: string | null;
                    workPhoneNumber?: string | null;
                    personalPhoneNumber?: string | null;
                    documentTypeId?: string;
                    workStatusId?: string;
                    status: string;
                    currentOperatorId?: string | null;
                    addressId?: string;
                    externalId?: string | null;
                };
            }
        >({
            query: ({ id, body }) => ({
                url: `/drivers/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Drivers'],
        }),
        updateDriverLegacyNumber: builder.mutation<void, { id: string; legacyNumber: string }>({
            query: ({ id, legacyNumber }) => ({
                url: `/drivers/${id}/legacy-number`,
                method: 'PATCH',
                body: { legacyNumber },
            }),
            invalidatesTags: ['Drivers'],
        }),
        getUnassignedDriver: builder.query<DriverApi, { logisticOperatorId: string }>({
            query: ({ logisticOperatorId }) => ({
                url: '/drivers/unassigned',
                headers: {
                    'x-logistic-operator-id': logisticOperatorId,
                },
            }),
            providesTags: ['Drivers'],
        }),
    }),
});

export default driversService;

export const {
    useGetDriversQuery,
    useGetDriverByIdQuery,
    useGetDriverProfilePictureQuery,
    useUploadDriverProfilePictureMutation,
    useGetDriverDocumentQuery,
    useGetDocumentTypesQuery,
    useEnableDriverMutation,
    useDisableDriverMutation,
    useSendOrResendDriverLogisticOperatorOtpMutation,
    useUpdateDriverMutation,
    useUpdateDriverLegacyNumberMutation,
    useUploadDriverDocumentMutation,
} = driversService;

export const useGetUnassignedDriverQuery = driversService.useGetUnassignedDriverQuery;
export const useLazyGetUnassignedDriverQuery = driversService.useLazyGetUnassignedDriverQuery;
